<script setup>
import { computed } from 'vue';
import Button from '@/Components/Button.vue';

const props = defineProps({
    events: {
        type: Array,
        default: () => []
    }
});

// Dummy events for visual purposes if no events provided
const dummyEvents = [
    {
        uuid: 1,
        title: "Afterwork QG MECS",
        date_full: "Vendredi 15 mars",
        start_time: "19h00",
        city: "Lyon 2ème",
        active_participants_count: 24,
        max_participants: 30,
        primary_image_url: "/images/events/event-1.jpg"
    },
    {
        uuid: 2,
        title: "Soirée jeux de société",
        date_full: "Samedi 16 mars",
        start_time: "20h30",
        city: "Lyon 6ème",
        active_participants_count: 12,
        max_participants: 15,
        primary_image_url: "/images/events/event-2.jpg"
    },
    {
        uuid: 3,
        title: "Brunch du dimanche",
        date_full: "Dimanche 17 mars",
        start_time: "11h00",
        city: "Lyon 1er",
        active_participants_count: 8,
        max_participants: 20,
        primary_image_url: "/images/events/event-3.jpg"
    }
];

const displayedEvents = computed(() => {
    return props.events.length > 0 ? props.events : dummyEvents;
});

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
};
</script>

<template>
    <section class="py-20 lg:py-32 bg-white">
        <div class="container mx-auto px-4">
            <!-- Section Header -->
            <div class="text-center mb-16" data-aos="fade-up">
                <h2 class="text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 leading-tight">
                    Nos prochains <span class="bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent">événements</span>
                </h2>
                <p class="text-base text-gray-600 leading-relaxed">
                    Rejoins-nous pour des moments conviviaux et authentiques
                </p>
            </div>

            <!-- Events Grid -->
            <div class="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
                <div
                    v-for="(event, index) in displayedEvents"
                    :key="event.uuid"
                    class="bg-white relative group cursor-pointer transition-shadow duration-300 hover:shadow-xl"
                    style="border-radius: 0 2rem 0 2rem; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);"
                    data-aos="fade-up"
                    :data-aos-delay="index * 100"
                >
                    <!-- Event Image -->
                    <div class="relative overflow-hidden" style="border-radius: 0 2rem 0 0;">
                        <img
                            :src="event.primary_image_url"
                            :alt="event.title"
                            class="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                            @error="$event.target.src = '/images/home/section-2.png'"
                        />
                        <!-- Participants Badge (only if max_participants is set) -->
                        <div
                            v-if="event.max_participants"
                            class="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1"
                        >
                            <svg class="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                            </svg>
                            <span>{{ event.active_participants_count }}/{{ event.max_participants }}</span>
                        </div>
                    </div>

                    <!-- Event Content -->
                    <div class="p-6">
                        <h3 class="text-xl font-bold mb-4">{{ event.title }}</h3>

                        <!-- Date -->
                        <div class="flex items-start gap-2 text-gray-600 mb-2">
                            <svg class="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                            </svg>
                            <div class="flex flex-col">
                                <span class="text-sm">{{ formatDate(event.date_full) }}</span>
                                <span class="text-sm">
                                    {{ event.start_time }}
                                    <template v-if="event.end_time">
                                        - {{ event.end_time }}
                                    </template>
                                </span>
                            </div>
                        </div>

                        <!-- Location -->
                        <div class="flex items-center gap-2 text-gray-600 mb-6">
                            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            <span class="text-sm">{{ event.city }}</span>
                        </div>

                        <!-- CTA Button -->
                        <Button
                            as="link"
                            :href="route('events.show', event.uuid)"
                            class="!rounded-full w-full flex items-center justify-center"
                        >
                            Voir l'événement
                        </Button>
                    </div>
                </div>
            </div>

            <!-- See All Events Button -->
            <div class="text-center">
                <Button
                    as="link"
                    :href="route('events.index')"
                    variant="light"
                    class="!rounded-full px-8 py-3"
                >
                    Voir tous les événements
                </Button>
            </div>
        </div>
    </section>
</template>
